'use client';

import { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Pencil, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface ProjectItem {
  id?: string;
  serialNumber: number;
  name: string;
  description?: string;
  quantity: number;
  unit?: string;
}

const PROJECT_ITEM_UNITS = [
  'boxes',
  'Cubic metres',
  'days',
  'dozens',
  'grams',
  'hours',
  'kg',
  'liters',
  'metres',
  'packs',
  'pairs',
  'pieces',
  'Quintal',
  'sets',
  'Sq meters',
  'Tons',
  'units',
] as const;

function projectItemUnitsForSelect(currentUnit?: string): string[] {
  const units: string[] = [...PROJECT_ITEM_UNITS];
  if (currentUnit?.trim() && !units.includes(currentUnit.trim())) {
    units.push(currentUnit.trim());
  }
  return units.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

interface ItemsEditorProps {
  items: ProjectItem[];
  onChange: (items: ProjectItem[]) => void;
  disabled?: boolean;
}

function itemSummary(item: ProjectItem) {
  const parts = [item.name.trim() || 'Untitled item'];
  if (item.quantity) {
    parts.push(`Qty: ${item.quantity.toLocaleString('en-IN')}`);
  }
  if (item.unit?.trim()) {
    parts.push(item.unit.trim());
  }
  return parts.join(' · ');
}

export function ItemsEditor({ items, onChange, disabled }: ItemsEditorProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const addItem = () => {
    const newItem: ProjectItem = {
      serialNumber: items.length + 1,
      name: '',
      quantity: 1,
    };
    onChange([...items, newItem]);
    setEditingIndex(items.length);
  };

  const removeItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    const reorderedItems = newItems.map((item, i) => ({ ...item, serialNumber: i + 1 }));
    onChange(reorderedItems);
    setEditingIndex((current) => {
      if (current === null) return null;
      if (current === index) return null;
      if (current > index) return current - 1;
      return current;
    });
  };

  const updateItem = (index: number, field: keyof ProjectItem, value: string | number) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    onChange(newItems);
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === items.length - 1) return;

    const newItems = [...items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    const reorderedItems = newItems.map((item, i) => ({ ...item, serialNumber: i + 1 }));
    onChange(reorderedItems);
    setEditingIndex((current) => {
      if (current === null) return null;
      if (current === index) return targetIndex;
      if (current === targetIndex) return index;
      return current;
    });
  };

  return (
    <div className="space-y-4">
      <Label className="text-base font-semibold">Items Required</Label>

      {items.length === 0 ? (
        <div className="border border-dashed rounded-lg p-6 text-center text-muted-foreground space-y-4">
          <div>
            <p>No items added yet.</p>
            <p className="text-sm">Add items required for this project.</p>
          </div>
          <Button type="button" variant="outline" onClick={addItem} disabled={disabled}>
            <Plus className="h-4 w-4 mr-1" />
            Add item
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, index) => {
            const isEditing = editingIndex === index;

            if (!isEditing) {
              return (
                <div
                  key={item.id || index}
                  className="flex items-center gap-3 border rounded-lg p-4 bg-muted/30"
                >
                  <span className="text-sm font-medium text-muted-foreground shrink-0">
                    #{item.serialNumber}
                  </span>
                  <p className="flex-1 text-sm font-medium">{itemSummary(item)}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingIndex(index)}
                    disabled={disabled}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1" />
                    Edit
                  </Button>
                </div>
              );
            }

            return (
              <div key={item.id || index} className="border rounded-lg p-4 bg-card space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => moveItem(index, 'up')}
                      disabled={disabled || index === 0}
                      className="h-8 w-8 rounded-lg disabled:opacity-30"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => moveItem(index, 'down')}
                      disabled={disabled || index === items.length - 1}
                      className="h-8 w-8 rounded-lg disabled:opacity-30"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">
                    #{item.serialNumber}
                  </span>
                  <div className="flex-1" />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setEditingIndex(null)}
                    disabled={disabled}
                    className="h-8 w-8 shrink-0 text-primary"
                    aria-label="Done editing item"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeItem(index)}
                    disabled={disabled}
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    aria-label="Remove item"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <Label htmlFor={`item-name-${index}`} className="text-xs">
                      Name *
                    </Label>
                    <Input
                      id={`item-name-${index}`}
                      value={item.name}
                      onChange={(e) => updateItem(index, 'name', e.target.value)}
                      placeholder="e.g., Laptops, Chairs, Books"
                      disabled={disabled}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor={`item-desc-${index}`} className="text-xs">
                      Description
                    </Label>
                    <Input
                      id={`item-desc-${index}`}
                      value={item.description || ''}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      placeholder="Optional description"
                      disabled={disabled}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`item-qty-${index}`} className="text-xs">
                      Quantity *
                    </Label>
                    <Input
                      id={`item-qty-${index}`}
                      type="number"
                      min={1}
                      value={item.quantity}
                      onChange={(e) =>
                        updateItem(index, 'quantity', parseInt(e.target.value, 10) || 1)
                      }
                      disabled={disabled}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`item-unit-${index}`} className="text-xs">
                      Unit
                    </Label>
                    <Select
                      value={item.unit || undefined}
                      onValueChange={(value) => updateItem(index, 'unit', value)}
                      disabled={disabled}
                    >
                      <SelectTrigger id={`item-unit-${index}`}>
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectItemUnitsForSelect(item.unit).map((unit) => (
                          <SelectItem key={unit} value={unit}>
                            {unit}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            );
          })}

          <Button
            type="button"
            variant="outline"
            onClick={addItem}
            disabled={disabled}
            className="w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add more item
          </Button>
        </div>
      )}
    </div>
  );
}
